<template>
  <!-- Only managers / owners may see the audit trail -->
  <section v-if="canView" class="change-history">
    <header class="ch-header" @click="collapsed = !collapsed">
      <div class="ch-title">
        <i class="fas fa-clock-rotate-left"></i>
        <span>{{ title }}</span>
        <span v-if="!loading" class="ch-count">{{ versions.length }} Ereignisse</span>
      </div>
      <i class="fas" :class="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
    </header>

    <div v-show="!collapsed" class="ch-body">
      <div v-if="loading" class="ch-state">
        <i class="fas fa-spinner fa-spin"></i> Lädt …
      </div>

      <div v-else-if="error" class="ch-state ch-error">
        <i class="fas fa-triangle-exclamation"></i> {{ error }}
      </div>

      <div v-else-if="!versions.length" class="ch-state">
        Keine Änderungen erfasst.
      </div>

      <ul v-else class="ch-list">
        <li v-for="v in versions" :key="v.id" class="ch-item">
          <div class="ch-item-top">
            <span class="ch-event" :class="`ch-event-${v.event}`">{{ eventLabel(v.event) }}</span>
            <span class="ch-author">
              <i class="fas fa-user"></i> {{ v.whodunnit_name }}
            </span>
            <span class="ch-time">{{ formatDateTime(v.created_at) }}</span>
          </div>

          <ul v-if="changedFields(v).length" class="ch-changes">
            <li v-for="f in changedFields(v)" :key="f.key" class="ch-change">
              <span class="ch-field">{{ fieldLabel(f.key) }}:</span>
              <span class="ch-old">{{ display(f.from) }}</span>
              <i class="fas fa-arrow-right"></i>
              <span class="ch-new">{{ display(f.to) }}</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import { fieldLabel as fieldLabelDe } from '@/utils/fieldLabels'

export default {
  name: 'ChangeHistory',

  props: {
    itemType: { type: String, required: true }, // e.g. 'Order', 'User', 'Customer'
    itemId: { type: [Number, String], default: null },
    title: { type: String, default: 'Änderungsverlauf' }
  },

  data() {
    return {
      versions: [],
      loading: false,
      error: null,
      collapsed: true
    }
  },

  computed: {
    ...mapGetters(['can']),

    canView() {
      return this.can('view_audit')
    }
  },

  mounted() {
    // Load eagerly so the "N Ereignisse" count is accurate before the panel is
    // expanded. The list itself stays collapsed by default.
    this.maybeLoad()
  },

  watch: {
    itemId: 'maybeLoad',
    // Retry on expand — covers a failed eager load and the case where `user`
    // isn't in the store yet at mount.
    collapsed(val) {
      if (!val) this.maybeLoad()
    }
  },

  methods: {
    maybeLoad() {
      if (this.canView && this.itemId && !this.versions.length && !this.loading) {
        this.load()
      }
    },

    async load() {
      this.loading = true
      this.error = null
      try {
        const res = await api.get('versions', {
          item_type: this.itemType,
          item_id: this.itemId
        })
        this.versions = res.data || []
      } catch (e) {
        this.error = 'Verlauf konnte nicht geladen werden.'
        console.error('ChangeHistory load error:', e)
      } finally {
        this.loading = false
      }
    },

    changedFields(version) {
      const changes = version.changes || {}
      return Object.keys(changes).map((key) => ({
        key,
        from: changes[key][0],
        to: changes[key][1]
      }))
    },

    eventLabel(event) {
      return { create: 'Erstellt', update: 'Geändert', destroy: 'Gelöscht' }[event] || event
    },

    fieldLabel(key) {
      return fieldLabelDe(key)
    },

    display(value) {
      if (value === null || value === undefined || value === '') return '—'
      if (value === true) return 'Ja'
      if (value === false) return 'Nein'
      return value
    },

    formatDateTime(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d)) return value
      return d.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.change-history {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  margin-top: 1.5rem;
  overflow: hidden;
}

.ch-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  cursor: pointer;
  user-select: none;
  background: #f9fafb;
  border-bottom: 1px solid #eef0f3;
}

.ch-header:hover { background: #f3f4f6; }

.ch-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
  color: #1f2937;
}

.ch-count {
  background: #e5e7eb;
  color: #374151;
  border-radius: 9999px;
  padding: 0.05rem 0.55rem;
  font-size: 0.78rem;
  font-weight: 600;
}

.ch-body { padding: 0.5rem 1.25rem 1rem; }

.ch-state {
  padding: 1rem 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.ch-error { color: #b91c1c; }

.ch-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ch-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f5;
}
.ch-item:last-child { border-bottom: none; }

.ch-item-top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.88rem;
}

.ch-event {
  font-weight: 600;
  border-radius: 6px;
  padding: 0.1rem 0.5rem;
  font-size: 0.78rem;
}
.ch-event-create { background: #dcfce7; color: #166534; }
.ch-event-update { background: #dbeafe; color: #1e40af; }
.ch-event-destroy { background: #fee2e2; color: #991b1b; }

.ch-author { color: #374151; }
.ch-author i { color: #9ca3af; margin-right: 0.25rem; }
.ch-time { color: #9ca3af; margin-left: auto; }

.ch-changes {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0 0 0 0.25rem;
}

.ch-change {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  font-size: 0.84rem;
  padding: 0.15rem 0;
  color: #4b5563;
}

.ch-field { font-weight: 600; color: #374151; }
.ch-old { color: #b91c1c; text-decoration: line-through; }
.ch-new { color: #166534; }
.ch-change .fa-arrow-right { color: #9ca3af; font-size: 0.72rem; }
</style>
